import { Box } from "@mui/material";
import ClientIntegrationsFilterBar, {
  type ClientIntegrationsFilters,
} from "components/integrations-beta/ClientIntegrationsFilterBar";
import ClientIntegrationsTable from "components/integrations-beta/ClientIntegrationsTable";
import RunsPagination from "components/integrations-beta/RunsPagination";
import ManagementPageShell from "components/management/ManagementPageShell";
import { MANAGEMENT_TABLE_AREA_SX } from "components/management/managementTheme";
import {
  ClientPortalIntegrationSortField,
  useGetClientPortalIntegrationsQuery,
} from "features/integrationsBeta/integrationsBeta";
import {
  useGetClientRunStatusesQuery,
  usePlatformEnumLabels,
} from "features/integrationsBeta/platformEnums";
import {
  CLIENT_FACING_TIMEZONE_DISPLAY_MODES,
  useTimezoneDisplayMode,
} from "features/integrationsBeta/timezoneDisplay";
import { useMemo, useState } from "react";
import { cycleSortField, type SortOrder } from "utils/sortUtils";
import Layout from "../components/Layout";

const DEFAULT_PAGE_SIZE = 50;

/**
 * /integrations-beta for a client portal user (CAL-5777), and for an admin
 * previewing one in Client View: the client's own integrations with the
 * client-facing status of their latest run. Reads only the client-portal
 * endpoints, so nothing here can leak an internal catalog or status.
 */
export default function ClientIntegrationsPage() {
  const [search, setSearch] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [sortBy, setSortBy] = useState<
    ClientPortalIntegrationSortField | undefined
  >();
  const [sortOrder, setSortOrder] = useState<SortOrder>("asc");
  const [filters, setFilters] = useState<ClientIntegrationsFilters>({});
  const [timezoneMode, setTimezoneMode] = useTimezoneDisplayMode(
    CLIENT_FACING_TIMEZONE_DISPLAY_MODES,
  );
  const { data: clientStatuses = [] } = useGetClientRunStatusesQuery();
  const { clientStatusLabel } = usePlatformEnumLabels({ skipInternal: true });

  const queryArgs = useMemo(
    () => ({
      page,
      page_size: pageSize,
      ...(search.trim() ? { search: search.trim() } : {}),
      ...filters,
      ...(sortBy ? { sort_by: sortBy, sort_order: sortOrder } : {}),
    }),
    [page, pageSize, search, filters, sortBy, sortOrder],
  );

  const { data, isLoading, isFetching, error } =
    useGetClientPortalIntegrationsQuery(queryArgs, {
      refetchOnMountOrArgChange: true,
    });

  const handleSortChange = (field: ClientPortalIntegrationSortField) => {
    const next = cycleSortField(sortBy, field, sortOrder);
    setSortBy(next.sortBy);
    setSortOrder(next.sortOrder);
    setPage(1);
  };

  const handleSearch = (term: string | null) => {
    setSearch(term ?? "");
    setPage(1);
  };

  const handleFiltersChange = (next: ClientIntegrationsFilters) => {
    setFilters(next);
    setPage(1);
  };

  return (
    <Layout title="Integrations" content="Integrations" noScroll>
      <ManagementPageShell title="Integrations">
        <Box mb={1.5} sx={{ flexShrink: 0 }}>
          <ClientIntegrationsFilterBar
            filters={filters}
            onChange={handleFiltersChange}
            search={search}
            onSearch={handleSearch}
            statuses={clientStatuses}
            timezoneMode={timezoneMode}
            onTimezoneModeChange={setTimezoneMode}
          />
        </Box>

        <Box sx={{ ...MANAGEMENT_TABLE_AREA_SX, flexDirection: "column" }}>
          <ClientIntegrationsTable
            integrations={data?.results ?? []}
            isLoading={isLoading}
            isFetching={isFetching}
            error={error}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSortChange={handleSortChange}
            timezoneMode={timezoneMode}
            clientStatusLabel={clientStatusLabel}
            pagination={
              <RunsPagination
                page={page}
                pageSize={pageSize}
                total={data?.count ?? 0}
                disabled={isFetching}
                onPageChange={setPage}
                onPageSizeChange={(limit) => {
                  setPageSize(limit);
                  setPage(1);
                }}
              />
            }
          />
        </Box>
      </ManagementPageShell>
    </Layout>
  );
}
